import {
  Badge,
  Box,
  Button,
  Flex,
  RefreshButton,
  Spinner,
  Stack,
  Table,
  Text,
} from "components/ui";
import {
  DialogActionTrigger,
  DialogBackdrop,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPositioner,
  DialogRoot,
  DialogTitle,
} from "components/ui/dialog";
import { useTranslation } from "hooks/useTranslation";
import { useCallback, useEffect, useState } from "react";
import {
  AutonomousAgentExecutionLog,
  AutonomousExecutionStatus,
  AutonomousRunStatus,
  autonomousAgentService,
} from "services/autonomousAgentService";
import { getThemeColor } from "theme/colors";

interface ExecutionLogsPanelProps {
  agentId: string;
}

const statusPalette: Record<AutonomousExecutionStatus, string> = {
  Pending: "gray",
  Running: "blue",
  Completed: "green",
  Failed: "red",
  WaitingForApproval: "yellow",
};

const formatDuration = (ms: number | null): string | null => {
  if (ms == null) return null;
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms} ms`;
};

/** Recent execution history for a single agent, most recent first. */
const ExecutionLogsPanel = ({ agentId }: ExecutionLogsPanelProps) => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState<AutonomousAgentExecutionLog[]>([]);
  const [loading, setLoading] = useState(true);

  // "View output" dialog — the run being viewed and its lazily-fetched result.
  const [viewing, setViewing] = useState<AutonomousAgentExecutionLog | null>(
    null,
  );
  const [runStatus, setRunStatus] = useState<AutonomousRunStatus | null>(null);
  const [runLoading, setRunLoading] = useState(false);
  const [runError, setRunError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await autonomousAgentService.getLogs(agentId, 20);
      setLogs(data);
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    load();
  }, [load]);

  const openOutput = useCallback(
    async (log: AutonomousAgentExecutionLog) => {
      setViewing(log);
      setRunStatus(null);
      setRunError(false);
      setRunLoading(true);
      try {
        const data = await autonomousAgentService.getRunOutput(agentId, log.id);
        setRunStatus(data);
      } catch {
        setRunError(true);
      } finally {
        setRunLoading(false);
      }
    },
    [agentId],
  );

  const closeOutput = () => setViewing(null);

  const fmt = (iso: string | null) =>
    iso ? new Date(iso).toLocaleString() : "—";

  // Only surface the attribution column once at least one run was triggered by a
  // per-user token — keeps the table narrow for the common company-token case.
  const hasAttribution = logs.some((log) => log.customerExternalId);

  const durationLabel = runStatus ? formatDuration(runStatus.durationMs) : null;

  return (
    <Stack gap={2}>
      <Flex justify="space-between" align="center">
        <Text fontSize="sm" fontWeight="semibold">
          {t("app.autonomousAgents.logs.title")}
        </Text>
        <RefreshButton onClick={load} loading={loading} iconOnly />
      </Flex>

      {logs.length === 0 ? (
        <Text fontSize="sm" color={getThemeColor("text.secondary")}>
          {t("app.autonomousAgents.logs.empty")}
        </Text>
      ) : (
        <Box overflowX="auto">
          <Table.Root size="sm">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>
                  {t("app.autonomousAgents.logs.status")}
                </Table.ColumnHeader>
                <Table.ColumnHeader>
                  {t("app.autonomousAgents.logs.startedAt")}
                </Table.ColumnHeader>
                <Table.ColumnHeader>
                  {t("app.autonomousAgents.logs.completedAt")}
                </Table.ColumnHeader>
                {hasAttribution && (
                  <Table.ColumnHeader>
                    {t("app.autonomousAgents.logs.triggeredFor")}
                  </Table.ColumnHeader>
                )}
                <Table.ColumnHeader>
                  {t("app.autonomousAgents.logs.error")}
                </Table.ColumnHeader>
                <Table.ColumnHeader>
                  {t("app.autonomousAgents.logs.output")}
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {logs.map((log) => (
                <Table.Row key={log.id}>
                  <Table.Cell>
                    <Badge colorPalette={statusPalette[log.status]}>
                      {t(
                        `app.autonomousAgents.status.${log.status.toLowerCase()}`,
                      )}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>{fmt(log.startedAt)}</Table.Cell>
                  <Table.Cell>{fmt(log.completedAt)}</Table.Cell>
                  {hasAttribution && (
                    <Table.Cell>
                      {log.customerExternalId ? (
                        <Badge colorPalette="blue">
                          {log.customerExternalId}
                        </Badge>
                      ) : (
                        <Text
                          fontSize="xs"
                          color={getThemeColor("text.secondary")}
                        >
                          —
                        </Text>
                      )}
                    </Table.Cell>
                  )}
                  <Table.Cell>
                    <Text fontSize="xs" color="red.400" lineClamp={2}>
                      {log.errorMessage ?? "—"}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Button
                      intent="ghost"
                      size="sm"
                      onClick={() => openOutput(log)}
                    >
                      {t("app.autonomousAgents.logs.viewOutput")}
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Box>
      )}

      <DialogRoot
        open={!!viewing}
        onOpenChange={(e: { open: boolean }) => !e.open && closeOutput()}
        size="lg"
      >
        <DialogBackdrop />
        <DialogPositioner>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {t("app.autonomousAgents.output.title")}
              </DialogTitle>
            </DialogHeader>

            <DialogBody>
              {runLoading ? (
                <Flex justify="center" align="center" py={8}>
                  <Spinner />
                </Flex>
              ) : runError ? (
                <Text fontSize="sm" color="red.400">
                  {t("app.autonomousAgents.output.loadError")}
                </Text>
              ) : runStatus ? (
                <Stack gap={3}>
                  <Flex gap={2} align="center" wrap="wrap">
                    <Badge colorPalette={statusPalette[runStatus.status]}>
                      {t(
                        `app.autonomousAgents.status.${runStatus.status.toLowerCase()}`,
                      )}
                    </Badge>
                    {durationLabel && (
                      <Text
                        fontSize="xs"
                        color={getThemeColor("text.secondary")}
                      >
                        {t("app.autonomousAgents.output.duration", {
                          duration: durationLabel,
                        })}
                      </Text>
                    )}
                  </Flex>

                  {runStatus.status === "Completed" ? (
                    runStatus.output ? (
                      <Box
                        borderWidth="1px"
                        borderColor={getThemeColor("border.default")}
                        borderRadius="md"
                        p={3}
                        maxH="400px"
                        overflowY="auto"
                        fontFamily="var(--font-mono)"
                        fontSize="xs"
                        whiteSpace="pre-wrap"
                        wordBreak="break-word"
                        color={getThemeColor("text.primary")}
                      >
                        {runStatus.output}
                      </Box>
                    ) : (
                      <Text
                        fontSize="sm"
                        color={getThemeColor("text.secondary")}
                      >
                        {t("app.autonomousAgents.output.empty")}
                      </Text>
                    )
                  ) : runStatus.status === "Failed" ? (
                    <Box
                      borderWidth="1px"
                      borderColor={getThemeColor("border.default")}
                      borderRadius="md"
                      p={3}
                      fontSize="sm"
                      color="red.400"
                      whiteSpace="pre-wrap"
                      wordBreak="break-word"
                    >
                      {runStatus.error ??
                        t("app.autonomousAgents.output.empty")}
                    </Box>
                  ) : (
                    <Text fontSize="sm" color={getThemeColor("text.secondary")}>
                      {t("app.autonomousAgents.output.stillRunning")}
                    </Text>
                  )}
                </Stack>
              ) : null}
            </DialogBody>

            <DialogFooter>
              <DialogActionTrigger asChild>
                <Button intent="secondary" onClick={closeOutput}>
                  {t("app.common.close")}
                </Button>
              </DialogActionTrigger>
            </DialogFooter>
          </DialogContent>
        </DialogPositioner>
      </DialogRoot>
    </Stack>
  );
};

export default ExecutionLogsPanel;
